import { useAutoTour } from 'components/tour/hooks/useTour'
import { LembreteProfissionalIdentificadorEnum } from 'graphql/types.generated'
import { useLembreteProfissionalComValidade } from 'hooks/lembrete-profissional/useLembreteProfissionalComValidade'
import { useCallback, useMemo } from 'react'

import { buildCidadaoTourSteps } from './tour-cidadao'

const TOUR_LEMBRETE_PROFISSIONAL_CIDADAO_ID = LembreteProfissionalIdentificadorEnum.TOUR_LEMBRETE_PROFISSIONAL_CIDADAO
const CARD_LEMBRETE_PROFISSIONAL_CIDADAO_ID = LembreteProfissionalIdentificadorEnum.CARD_LEMBRETE_PROFISSIONAL_CIDADAO
const CARD_LEMBRETE_PROFISSIONAL_EXPIRES_IN_DAYS = 30

interface UseCidadaoCreateTourParams {
  usuarioId?: string
  isUsuarioAps: boolean
  isNovoCadastro: boolean
  isCadastroOrigemForaAps: boolean
  isCadsusButtonVisible: boolean
  isAutoTourBlocked?: boolean
}

export function useCidadaoCreateTour({
  usuarioId,
  isUsuarioAps,
  isNovoCadastro,
  isCadastroOrigemForaAps,
  isCadsusButtonVisible,
  isAutoTourBlocked = false,
}: Readonly<UseCidadaoCreateTourParams>) {
  const isModoSimplificado = !isUsuarioAps && (isNovoCadastro || isCadastroOrigemForaAps)

  const cidadaoAutoTourSteps = useMemo(
    () =>
      buildCidadaoTourSteps({
        isModoSimplificado,
        isNovoCadastro,
        isCadsusButtonVisible,
      }),
    [isModoSimplificado, isNovoCadastro, isCadsusButtonVisible]
  )

  const cidadaoContextHelperTourSteps = useMemo(
    () =>
      buildCidadaoTourSteps({
        isModoSimplificado,
        isNovoCadastro,
        isCadsusButtonVisible,
        includePageTitle: false,
      }),
    [isModoSimplificado, isNovoCadastro, isCadsusButtonVisible]
  )

  const { start, startPersisted, loading: loadingTourCidadao } = useAutoTour(
    TOUR_LEMBRETE_PROFISSIONAL_CIDADAO_ID,
    cidadaoAutoTourSteps,
    // TourProvider bloqueia eventos fora do tooltip; aguardar os modais da tela fecharem antes de iniciar automaticamente.
    { autoStart: !isAutoTourBlocked }
  )

  const { isAtivo: isCardLembreteAtivo, loading: loadingCardLembrete } = useLembreteProfissionalComValidade({
    usuarioId,
    identificador: CARD_LEMBRETE_PROFISSIONAL_CIDADAO_ID,
    diasValidade: CARD_LEMBRETE_PROFISSIONAL_EXPIRES_IN_DAYS,
  })

  const startCardTour = useCallback(() => {
    const hasStartedPersisted = startPersisted(cidadaoContextHelperTourSteps)

    if (!hasStartedPersisted) {
      start(cidadaoContextHelperTourSteps)
    }
  }, [cidadaoContextHelperTourSteps, start, startPersisted])

  return {
    isCardLembreteAtivo,
    isLembretesLoading: loadingTourCidadao || loadingCardLembrete,
    startCardTour,
  }
}
